<script setup lang="ts">
import type LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useBulkDeleteDtwins } from "~/modules/dtwin/composables/useBulkDeleteDtwins";
import { useDeleteDtwin } from "~/modules/dtwin/composables/useDeleteDtwin";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  dtwinUids: string | string[];
  organizationCode?: number;
  buttonClass?: string[];
}>();

const { t } = useI18n();
const {
  isLoading: isLoadingDeleteDtwin,
  error: errorDeleteDtwin,
  deleteDtwin,
} = useDeleteDtwin({
  organizationCode: props.organizationCode,
});
const {
  isLoading: isLoadingBulkDelete,
  error: errorBulkDelete,
  bulkDeleteDtwins,
} = useBulkDeleteDtwins({ organizationCode: props.organizationCode });

const emit = defineEmits(["delete"]);

const showConfirm = ref(false);

const onClickAction = () => {
  showConfirm.value = true;
};

async function onDeleteDtwin() {
  const error = ref<LxcError | undefined>();
  if (!Array.isArray(props.dtwinUids)) {
    await deleteDtwin(props.dtwinUids);
    if (errorDeleteDtwin.value) {
      error.value = errorDeleteDtwin.value;
    }
  } else {
    await bulkDeleteDtwins(props.dtwinUids);
    if (errorBulkDelete.value) {
      error.value = errorBulkDelete.value;
    }
  }

  if (error.value) {
    error.value.notify(NotificationKey.removeError);
  } else {
    showNotificationSuccess(t(NotificationKey.success));
    emit("delete");
  }
  showConfirm.value = false;
}

const buttonClassComputed = computed(() => {
  const buttonClass = ["!px-0", "!py-0"];
  if (props.buttonClass) {
    buttonClass.push(...props.buttonClass);
  }
  return buttonClass;
});
</script>

<template>
  <lxc-button
    html-type="button"
    type="borderless"
    :class="buttonClassComputed"
    :title="t('dtwins.action.delete.tooltip')"
    :icon="
      !isLoadingDeleteDtwin && !isLoadingBulkDelete
        ? ILxcLightTrash2
        : undefined
    "
    :is-loading="isLoadingDeleteDtwin || isLoadingBulkDelete"
    @click.stop.prevent="onClickAction"
  />
  <confirm-modal
    :is-dialog-visible="showConfirm"
    :title="t('dtwins.action.delete.confirm.title')"
    :description="t('dtwins.action.delete.confirm.message')"
    :cancel-label="t('button.cancel')"
    :ok-label="t('button.confirm')"
    :icon="ILxcLightTrash2"
    icon-color-theme="error"
    color-theme="danger"
    @confirm="onDeleteDtwin"
    @cancel="showConfirm = false"
  />
</template>

<style scoped lang="scss">
button {
  :deep(svg) {
    height: 20px;
    width: 20px;
  }
}
</style>
